<template>
  <div class="messengers-admin">
    <div class="messengers-admin__toolbar">
      <el-input
        v-model="search"
        placeholder="Поиск по аккаунту, домену, каналу"
        clearable
        @input="debouncedSearch"
      />
    </div>

    <div v-if="loading" class="messengers-admin__loading">
      <el-icon class="is-loading"><Loading /></el-icon>
      <span>Загружаем аккаунты и подписки каналов</span>
    </div>

    <el-table
      v-else
      :data="accounts"
      style="width: 100%"
      row-key="installation_id"
      :expand-row-keys="expandedRowKeys"
      @expand-change="handleExpandChange"
    >
      <el-table-column type="expand">
        <template #default="scope">
          <div class="messengers-admin__expand">
            <div class="messengers-admin__section">
              <div class="messengers-admin__section-title">Каналы</div>
              <div v-if="scope.row.channels.length === 0" class="messengers-admin__empty">
                Каналы не найдены
              </div>
              <div v-for="channel in scope.row.channels" :key="channel.id" class="messengers-admin__channel-card">
                <div class="messengers-admin__channel-top">
                  <div class="messengers-admin__channel-title">{{ channel.title || channel.type }}</div>
                  <el-tag :type="tagType(channel.status)" effect="light">{{ channel.status }}</el-tag>
                </div>
                <div class="messengers-admin__channel-meta">
                  <div><b>Тип:</b> {{ channel.type }}</div>
                  <div><b>External ID:</b> {{ channel.external_id }}</div>
                  <div><b>Телефон:</b> {{ channel.profile_phone || channel.settings?.phone || '—' }}</div>
                  <div><b>Обновлен:</b> {{ formatDate(channel.updated_at) }}</div>
                </div>
                <div class="messengers-admin__subscription-actions">
                  <el-button
                    v-if="!channel.subscription"
                    type="success"
                    size="small"
                    :loading="activationLoadingId === channel.id"
                    @click="openSubscriptionDialog('activate', channel)"
                  >
                    Активировать подписку
                  </el-button>
                  <div v-else class="messengers-admin__channel-linked">
                    Подписка уже создана
                  </div>
                </div>
              </div>
            </div>

            <div class="messengers-admin__section">
              <div class="messengers-admin__section-head">
                <div class="messengers-admin__section-title">Подписки</div>
                <el-button
                  v-if="scope.row.subscriptions.length"
                  type="primary"
                  plain
                  size="small"
                  :loading="bulkLoadingAccountId === scope.row.account_id"
                  @click="extendAllSubscriptions(scope.row)"
                >
                  Продлить все на 1 месяц
                </el-button>
              </div>

              <div v-if="scope.row.subscriptions.length === 0" class="messengers-admin__empty">
                Подписок пока нет
              </div>

              <div
                v-for="subscription in scope.row.subscriptions"
                :key="subscription.id"
                class="messengers-admin__subscription-card"
              >
                <div class="messengers-admin__channel-top">
                  <div>
                    <div class="messengers-admin__channel-title">
                      {{ subscription.title || subscription.messenger_type }}
                    </div>
                    <div class="messengers-admin__subscription-subtitle">
                      {{ subscription.profile_phone || 'Без номера' }}
                    </div>
                  </div>
                  <el-tag :type="subscriptionTagType(subscription.status, subscription.is_paid)" effect="light">
                    {{ subscriptionStatusText(subscription) }}
                  </el-tag>
                </div>
                <div class="messengers-admin__channel-meta">
                  <div><b>Тип:</b> {{ subscription.messenger_type }}</div>
                  <div><b>Статус:</b> <span :class="statusTextClass(subscription)">{{ subscriptionStatusText(subscription) }}</span></div>
                  <div><b>Подписка до:</b> {{ formatDate(subscription.paid_to_ts) }}</div>
                  <div><b>Осталось:</b> {{ daysLeftText(subscription) }}</div>
                  <div><b>Цена:</b> {{ formatMoney(subscription.price_per_month, subscription.currency) }}</div>
                  <div><b>Привязан:</b> {{ subscription.current_messenger_id ? 'Да' : 'Нет' }}</div>
                </div>
                <div class="messengers-admin__subscription-actions">
                  <el-button
                    :type="subscription.is_paid ? 'primary' : 'success'"
                    size="small"
                    :loading="actionLoadingId === subscription.id"
                    @click="openSubscriptionDialog(subscription.is_paid ? 'extend' : 'activate', subscription)"
                  >
                    {{ subscription.is_paid ? 'Продлить' : 'Активировать' }}
                  </el-button>
                  <el-button
                    v-if="subscription.is_paid"
                    type="danger"
                    plain
                    size="small"
                    :loading="resetLoadingId === subscription.id"
                    @click="resetSubscription(subscription)"
                  >
                    Сбросить
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Account ID" prop="account_id" width="130" />
      <el-table-column label="Домен" prop="subdomain" min-width="160" />
      <el-table-column label="Название" prop="title" min-width="180" />
      <el-table-column label="Каналов" width="110">
        <template #default="scope">
          {{ scope.row.channels.length }}
        </template>
      </el-table-column>
      <el-table-column label="Активных" width="110">
        <template #default="scope">
          {{ scope.row.channels.filter((item) => item.status === 'active').length }}
        </template>
      </el-table-column>
      <el-table-column label="Обновлен" width="130">
        <template #default="scope">
          {{ formatDate(scope.row.updated_at) }}
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="subscriptionDialog.visible"
      :title="subscriptionDialogTitle"
      width="420px"
      destroy-on-close
    >
      <div class="messengers-admin__dialog-body">
        <div class="messengers-admin__dialog-label">Дата окончания подписки</div>
        <el-date-picker
          v-model="subscriptionDialog.targetDate"
          type="date"
          placeholder="Выберите дату"
          format="DD.MM.YYYY"
          value-format="YYYY-MM-DD"
          :disabled-date="disablePastDates"
          style="width: 100%;"
        />
      </div>
      <template #footer>
        <el-button @click="closeSubscriptionDialog">Отмена</el-button>
        <el-button
          type="primary"
          :loading="subscriptionDialog.loading"
          @click="submitSubscriptionDialog"
        >
          Сохранить
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Loading } from '@element-plus/icons-vue';
import store from "@/components/store/store";

let searchTimer = null;

export default {
  name: 'MessengerAccounts',
  components: {
    Loading,
  },
  data() {
    return {
      search: '',
      actionLoadingId: 0,
      bulkLoadingAccountId: 0,
      activationLoadingId: 0,
      resetLoadingId: 0,
      expandedRowKeys: [],
      subscriptionDialog: {
        visible: false,
        mode: 'activate',
        targetDate: '',
        target: null,
        loading: false,
      },
    };
  },
  computed: {
    accounts() {
      return store.state.messengerAccounts;
    },
    loading() {
      return store.state.messengerAccountsLoading;
    },
    subscriptionDialogTitle() {
      return this.subscriptionDialog.mode === 'activate'
        ? 'Активация подписки'
        : 'Изменение даты подписки';
    }
  },
  async mounted() {
    await store.dispatch('fetchMessengerAccounts');
  },
  methods: {
    debouncedSearch() {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
      searchTimer = setTimeout(() => {
        store.dispatch('fetchMessengerAccounts', { search: this.search });
      }, 250);
    },
    formatDate(value) {
      const ts = Number(value || 0);
      if (!ts) {
        return '—';
      }
      return moment(ts * 1000).format('DD.MM.YYYY');
    },
    formatMoney(value, currency = 'RUB') {
      return `${Number(value || 0)} ${currency}`;
    },
    tagType(status) {
      if (status === 'active') {
        return 'success';
      }
      if (status === 'disabled') {
        return 'danger';
      }
      return 'info';
    },
    subscriptionTagType(status, isPaid) {
      if (status === 'detached') {
        return 'warning';
      }
      if (isPaid) {
        return 'success';
      }
      if (status === 'blocked') {
        return 'danger';
      }
      return 'info';
    },
    subscriptionStatusText(subscription) {
      if (subscription.is_paid) {
        return 'Оплачено';
      }
      if (subscription.status === 'detached') {
        return 'Отвязан';
      }
      if (subscription.status === 'blocked') {
        return 'Заблокирован';
      }
      return 'Не оплачено';
    },
    async extendAllSubscriptions(account) {
      const { value } = await ElMessageBox.prompt(
        `На сколько месяцев продлить все каналы аккаунта ${account.subdomain || account.account_id}?`,
        'Продление всех каналов',
        {
          confirmButtonText: 'Продлить все',
          cancelButtonText: 'Отмена',
          inputValue: '1',
          inputPattern: /^[1-9]\d*$/,
          inputErrorMessage: 'Введите целое число от 1',
        }
      ).catch(() => ({ value: null }));

      if (!value) {
        return;
      }

      this.bulkLoadingAccountId = account.account_id;
      try {
        for (const subscription of account.subscriptions) {
          await store.dispatch('extendMessengerSubscription', {
            subscriptionId: subscription.id,
            months: Number(value),
            search: this.search,
          });
        }
        ElMessage.success('Все подписки продлены');
      } catch (error) {
        ElMessage.error(error?.message || 'Не удалось продлить часть подписок');
      } finally {
        this.bulkLoadingAccountId = 0;
      }
    },
    daysLeftText(subscription) {
      if (!subscription?.is_paid) {
        return 'Не активно';
      }
      return `${subscription.days_left} дн.`;
    },
    statusTextClass(subscription) {
      if (subscription.status === 'blocked') {
        return 'is-danger';
      }
      if (subscription.status === 'detached') {
        return 'is-warning';
      }
      if (subscription.is_paid) {
        return 'is-success';
      }
      return 'is-muted';
    },
    disablePastDates(date) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      return date.getTime() < startOfToday.getTime();
    },
    openSubscriptionDialog(mode, target) {
      const paidToTs = Number(target?.paid_to_ts || 0);
      const fallbackDate = moment().format('YYYY-MM-DD');
      const targetDate = paidToTs ? moment(paidToTs * 1000).format('YYYY-MM-DD') : fallbackDate;

      this.subscriptionDialog.visible = true;
      this.subscriptionDialog.mode = mode;
      this.subscriptionDialog.target = target;
      this.subscriptionDialog.targetDate = targetDate;
      this.subscriptionDialog.loading = false;
    },
    closeSubscriptionDialog() {
      this.subscriptionDialog.visible = false;
      this.subscriptionDialog.mode = 'activate';
      this.subscriptionDialog.targetDate = '';
      this.subscriptionDialog.target = null;
      this.subscriptionDialog.loading = false;
    },
    async submitSubscriptionDialog() {
      if (!this.subscriptionDialog.targetDate) {
        ElMessage.error('Выберите дату окончания подписки');
        return;
      }

      const target = this.subscriptionDialog.target;
      if (!target) {
        return;
      }

      const isActivate = this.subscriptionDialog.mode === 'activate';
      const isExistingSubscriptionTarget =
        Object.prototype.hasOwnProperty.call(target, 'current_messenger_id')
        || Object.prototype.hasOwnProperty.call(target, 'price_per_month')
        || Object.prototype.hasOwnProperty.call(target, 'paid_to_ts');
      const payload = {
        targetDate: this.subscriptionDialog.targetDate,
        search: this.search,
      };

      if (isActivate && !isExistingSubscriptionTarget) {
        payload.messengerId = Number(target.id || 0);
      } else {
        payload.subscriptionId = Number(target.id || 0);
      }

      if (isActivate) {
        this.activationLoadingId = Number(target.id || 0);
      } else {
        this.actionLoadingId = Number(target.id || 0);
      }
      this.subscriptionDialog.loading = true;

      try {
        await store.dispatch('extendMessengerSubscription', payload);
        this.restoreExpandedRowByTarget(target);
        ElMessage.success(isActivate ? 'Подписка активирована' : 'Дата подписки обновлена');
        this.closeSubscriptionDialog();
      } catch (error) {
        ElMessage.error(error?.message || 'Не удалось сохранить подписку');
      } finally {
        this.subscriptionDialog.loading = false;
        this.activationLoadingId = 0;
        this.actionLoadingId = 0;
      }
    },
    async resetSubscription(subscription) {
      try {
        await ElMessageBox.confirm(
          `Полностью обнулить подписку для ${subscription.title || subscription.messenger_type}?`,
          'Сброс подписки',
          {
            confirmButtonText: 'Сбросить',
            cancelButtonText: 'Отмена',
            type: 'warning',
          }
        );
      } catch (_) {
        return;
      }

      this.resetLoadingId = Number(subscription.id || 0);
      try {
        await store.dispatch('resetMessengerSubscription', {
          subscriptionId: subscription.id,
          search: this.search,
        });
        this.restoreExpandedRowByTarget(subscription);
        ElMessage.success('Подписка сброшена');
      } catch (error) {
        ElMessage.error(error?.message || 'Не удалось сбросить подписку');
      } finally {
        this.resetLoadingId = 0;
      }
    },
    handleExpandChange(row, expandedRows) {
      this.expandedRowKeys = expandedRows.map((item) => item.installation_id);
    },
    restoreExpandedRowByTarget(target) {
      const installationId = Number(target?.installation_id || 0);
      if (!installationId) {
        return;
      }
      if (!this.expandedRowKeys.includes(installationId)) {
        this.expandedRowKeys = [...this.expandedRowKeys, installationId];
      }
    }
  }
}
</script>

<style scoped>
.messengers-admin {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.messengers-admin__toolbar {
  display: flex;
  justify-content: flex-start;
}

.messengers-admin__toolbar .el-input {
  max-width: 360px;
}

.messengers-admin__loading {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #5f6b85;
  font-size: 15px;
}

.messengers-admin__expand {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 8px 12px;
  background: #f8fbff;
  border-radius: 10px;
}

.messengers-admin__section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.messengers-admin__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.messengers-admin__section-title {
  text-align: left;
  font-size: 15px;
  font-weight: 700;
}

.messengers-admin__channel-card {
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #dce6f2;
  border-radius: 10px;
}

.messengers-admin__subscription-card {
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #dce6f2;
  border-radius: 10px;
}

.messengers-admin__channel-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.messengers-admin__channel-title {
  font-size: 15px;
  font-weight: 700;
}

.messengers-admin__subscription-subtitle {
  margin-top: 4px;
  color: #7c8aa5;
  font-size: 13px;
  text-align: left;
}

.messengers-admin__channel-meta {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
  text-align: left;
  font-size: 13px;
}

.messengers-admin__empty {
  text-align: left;
  color: #7c8aa5;
}

.messengers-admin__subscription-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-start;
  gap: 8px;
}

.messengers-admin__channel-linked {
  color: #67c23a;
  font-size: 13px;
  font-weight: 600;
}

.messengers-admin__dialog-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.messengers-admin__dialog-label {
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #4d5875;
}

.is-success {
  color: #67c23a;
  font-weight: 600;
}

.is-warning {
  color: #e6a23c;
  font-weight: 600;
}

.is-danger {
  color: #f56c6c;
  font-weight: 600;
}

.is-muted {
  color: #7c8aa5;
  font-weight: 600;
}
</style>
